import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { createRafScheduler } from '@/lib/dom/createRafScheduler';
import { applyReframePan } from '@/lib/layers/applyReframePan';
import {
  applyTextDrag, isPointInTextBlock, type SlidePlacementGeometry,
} from '@/lib/quoteCarousel/slidePlacement';
import type { QuoteSlideProps } from '@/lib/quoteCarousel/buildQuoteSlideProps';
import type { QuoteBackgroundFrame, QuoteTextPosition } from '@/lib/quoteCarousel/types';
import { computeQuoteSlideLayout } from '@/remotion/carousel/quoteSlideLayout';
import { cn } from '@/lib/utils';
import { useFontLoadVersion } from '@/lib/fonts/fontLoadVersion';

export type SlidePlacementPatch =
  | { textPosition: QuoteTextPosition }
  | { backgroundFrame: QuoteBackgroundFrame };

interface SlideDragOverlayProps {
  /** Props currently drawn by the Player (draft included). */
  slideProps: QuoteSlideProps;
  /** Live draft while dragging, at most once per animation frame; null = drag ended. */
  onDraft: (patch: SlidePlacementPatch | null) => void;
  /** Final value on pointerup (only when something moved). */
  onCommit: (patch: SlidePlacementPatch) => void;
}

type DragState = {
  mode: 'text' | 'pan';
  startX: number;
  startY: number;
  rect: DOMRect;
  startCenter: QuoteTextPosition;
  startFrame: QuoteBackgroundFrame;
  /** Geometry at pointerdown: the block size does not change while dragging. */
  geometry: SlidePlacementGeometry;
  latest: SlidePlacementPatch | null;
};

const NEUTRAL_FRAME: QuoteBackgroundFrame = { scale: 1, offsetX: 0, offsetY: 0 };

/**
 * Transparent layer over the big slide preview (sibling of the Player, the
 * LayerCanvasEditor/SatisfyingCellEditor pattern): drag the text block to
 * move it, drag anywhere else on a frame background to pan it (zoom first —
 * at 1× there is nothing to pan). Updates are coalesced to one per frame and
 * committed on pointerup.
 */
export const SlideDragOverlay: React.FC<SlideDragOverlayProps> = ({ slideProps, onDraft, onCommit }) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const [dragging, setDragging] = useState<DragState['mode'] | null>(null);
  const [hoverText, setHoverText] = useState(false);

  // Re-measure when a font finishes loading: the slide re-fits in the real
  // font, and the hit box / outline must match what is drawn.
  const fontLoadVersion = useFontLoadVersion();
  const layout = useMemo(
    () => computeQuoteSlideLayout(slideProps),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [slideProps, fontLoadVersion],
  );
  const geometry = useMemo(() => ({
    width: slideProps.width,
    height: slideProps.height,
    padding: layout.padding,
    block: layout.block,
  }), [layout, slideProps.height, slideProps.width]);

  // Callbacks go through refs so the window listeners stay stable for the
  // whole gesture even though every draft re-renders this overlay.
  const onDraftRef = useRef(onDraft);
  onDraftRef.current = onDraft;
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;
  const schedulerRef = useRef(createRafScheduler<SlidePlacementPatch>((patch) => onDraftRef.current(patch)));

  const toPoint = (clientX: number, clientY: number, rect: DOMRect): QuoteTextPosition => ({
    x: rect.width > 0 ? (clientX - rect.left) / rect.width : 0,
    y: rect.height > 0 ? (clientY - rect.top) / rect.height : 0,
  });

  const onPointerMove = useCallback((event: PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dxFrac = drag.rect.width > 0 ? (event.clientX - drag.startX) / drag.rect.width : 0;
    const dyFrac = drag.rect.height > 0 ? (event.clientY - drag.startY) / drag.rect.height : 0;
    const patch: SlidePlacementPatch = drag.mode === 'text'
      ? {
        textPosition: applyTextDrag({
          ...drag.geometry, center: drag.startCenter, dxFrac, dyFrac,
        }),
      }
      : { backgroundFrame: applyReframePan({ frame: drag.startFrame, dxFrac, dyFrac }) };
    drag.latest = patch;
    schedulerRef.current.schedule(patch);
  }, []);

  const endDrag = useCallback(function end() {
    const drag = dragRef.current;
    dragRef.current = null;
    setDragging(null);
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', end);
    window.removeEventListener('pointercancel', end);
    schedulerRef.current.cancel();
    if (drag?.latest) onCommitRef.current(drag.latest);
    onDraftRef.current(null);
  }, [onPointerMove]);

  // Never leave window listeners behind an unmounted preview.
  useEffect(() => () => {
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', endDrag);
    window.removeEventListener('pointercancel', endDrag);
    schedulerRef.current.cancel();
  }, [endDrag, onPointerMove]);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    // Primary button only: right-click belongs to the browser menu.
    if (event.button !== 0 || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const onText = isPointInTextBlock(toPoint(event.clientX, event.clientY, rect), geometry);
    if (!onText && !slideProps.backgroundUrl) return;
    event.preventDefault();
    dragRef.current = {
      mode: onText ? 'text' : 'pan',
      startX: event.clientX,
      startY: event.clientY,
      rect,
      startCenter: layout.center,
      startFrame: slideProps.backgroundFrame ?? NEUTRAL_FRAME,
      geometry,
      latest: null,
    };
    setDragging(onText ? 'text' : 'pan');
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
  };

  const onHover = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const next = isPointInTextBlock(toPoint(event.clientX, event.clientY, rect), geometry);
    if (next !== hoverText) setHoverText(next);
  };

  const { block } = layout;
  const showOutline = hoverText || dragging === 'text';
  return (
    <div
      ref={containerRef}
      data-testid="quote-slide-drag-overlay"
      aria-label={t('quoteCarousel.placement.dragHint')}
      title={t('quoteCarousel.placement.dragHint')}
      className={cn(
        'absolute inset-0 touch-none select-none',
        showOutline ? 'cursor-move' : slideProps.backgroundUrl ? 'cursor-grab' : 'cursor-default',
        dragging === 'pan' && 'cursor-grabbing',
      )}
      onPointerDown={onPointerDown}
      onPointerMove={onHover}
      onPointerLeave={() => setHoverText(false)}
    >
      {showOutline && (
        <div
          data-testid="quote-slide-text-outline"
          className="pointer-events-none absolute rounded-sm border-2 border-dashed border-primary"
          style={{
            left: `${(block.left / slideProps.width) * 100}%`,
            top: `${(block.top / slideProps.height) * 100}%`,
            width: `${(block.width / slideProps.width) * 100}%`,
            height: `${(block.height / slideProps.height) * 100}%`,
          }}
        />
      )}
    </div>
  );
};

export default SlideDragOverlay;
